/**
 * Pure logic for the EDUS-2528 selection-lock revert pass. Extracted from
 * `CdwSsrmGridComponent.onSelectionChanged`'s lock-active branch so it can
 * be unit-tested without importing the full component (which transitively
 * pulls `AgGridModule` and breaks under vitest's ESM module system — see
 * `sql-ssrm-datasource.factory.spec.ts` header for the same constraint).
 *
 * Uses structural typing so this module has no AG-Grid runtime dependency.
 * `RevertableNode` is satisfied by AG-Grid's `IRowNode` at the call site.
 */

export interface RevertableNode {
  readonly data: unknown;
  // AG-Grid's IRowNode.isSelected returns `boolean | undefined` — undefined
  // for indeterminate states (group-row partial selection). For per-row
  // SSRM checkboxes the indeterminate case shouldn't arise, but we accept
  // the broader type to keep the interface assignable from `RowNode<T>`
  // without casts. `undefined !== truth` (where truth is boolean) is
  // always truthy, so the comparison falls through to setSelected which
  // is fine — the call is idempotent on already-correct state.
  isSelected(): boolean | undefined;
  setSelected(
    value: boolean,
    clearSelection: boolean,
    suppressFinishActions: boolean
  ): void;
}

/**
 * Revert a single node's selection state back to `node.data.isSelected`
 * (the server truth). No-op when there's no authoritative value to revert to:
 *
 *   - `node.data` is absent, OR
 *   - the `isSelected` key isn't on the data, OR
 *   - the `isSelected` value is explicitly `undefined`.
 *
 * All three cases collapse to "leave the node's AG-Grid state alone" —
 * force-deselecting would silently destroy any selection the user made
 * before the lock activated. The idempotency check (skip when
 * `isSelected() === truth`) is the recursion floor.
 *
 * The third arg to `setSelected` is LOAD-BEARING: `suppressFinishActions:
 * true` prevents AG-Grid from re-firing `selectionChanged`, which would
 * cause infinite re-entry through `onSelectionChanged`.
 */
export function revertNodeSelectionToServerTruth(node: RevertableNode): void {
  if (!node.data) return;
  if (!('isSelected' in (node.data as object))) return;
  const rawTruth = (node.data as { isSelected?: boolean }).isSelected;
  if (rawTruth === undefined) return;
  const truth = rawTruth === true;
  if (node.isSelected() !== truth) {
    node.setSelected(
      truth,
      /* clearSelection */ false,
      /* suppressFinishActions */ true
    );
  }
}
